import React, { Fragment } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import Spinner from '@atlaskit/spinner/spinner';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { GenericErrorBoundary } from '@confluence/error-boundary/entry-points/GenericErrorBoundary.ts';
import type { ExtensionHandlerProps } from '@confluence/fabric-extension-lib/entry-points/commonExtensionTypes.ts';
import {
	getExperienceName,
	getMacroAttributesFromADFNode,
} from '@confluence/macro-tracker/entry-points/MacroExperienceTracker.ts';
import { MacroExperienceFailure } from '@confluence/macro-tracker/entry-points/MacroExperienceFailure.ts';
import { MacroExperienceSuccess } from '@confluence/macro-tracker/entry-points/MacroExperienceSuccess.ts';
import type { NestedAdfRendererWrapper } from '@confluence/macro-utils/entry-points/nestedAdfRenderer.ts';

import { i18n } from './blogPostsMacroi18n.ts';
import { useBlogPostsMacroData } from './hooks/useBlogPostsMacroData.ts';
import type { BlogPostsMacroParams } from './blogPostMacroTypes.ts';
import { TitlesList } from './components/TitlesList.tsx';
import { ExcerptsList } from './components/ExcerptsList.tsx';
import { EmptyState } from './components/EmptyState.tsx';

export type BlogPostsMacroRendererProps = {
	macroDefaultProps: ExtensionHandlerProps;
	macroParams?: BlogPostsMacroParams;
	RendererWrapper: NestedAdfRendererWrapper;
};

const BlogPostsMacroRendererComponent = ({
	macroDefaultProps,
	macroParams,
	RendererWrapper,
}: BlogPostsMacroRendererProps) => {
	const { formatMessage } = useIntl();
	const {
		contentMode,
		error,
		hasNextPage,
		isLoadingMore,
		loadMore,
		loadMoreError,
		loading,
		nodes,
		serializedMacroParameters,
		spaceKey,
	} = useBlogPostsMacroData(macroParams, macroDefaultProps);
	const { contentId, extensionKey, mode, node } = macroDefaultProps;
	const attributes = macroDefaultProps.attributes ?? getMacroAttributesFromADFNode(node);
	const experienceName = getExperienceName(mode, node);

	if (loading) {
		return (
			<Box padding="space.300" testId="blog-posts-loading">
				<Stack alignInline="center">
					<Spinner size="medium" label={formatMessage(i18n.loading)} />
				</Stack>
			</Box>
		);
	}

	if (error) {
		return (
			<Fragment>
				<Box padding="space.300" testId="blog-posts-error">
					<Stack alignInline="center">
						<Text>
							<FormattedMessage {...i18n.error} />
						</Text>
					</Stack>
				</Box>
				<MacroExperienceFailure
					name={experienceName}
					contentId={contentId}
					extensionKey={extensionKey}
					mode={mode}
					attributes={attributes}
					error={error}
				/>
			</Fragment>
		);
	}

	let content: React.ReactNode;
	if (nodes.length === 0) {
		content = <EmptyState spaceKey={spaceKey} />;
	} else if (contentMode === 'titles') {
		content = <TitlesList nodes={nodes} />;
	} else {
		content = (
			<ExcerptsList nodes={nodes} contentMode={contentMode} RendererWrapper={RendererWrapper} />
		);
	}

	return (
		<Fragment>
			<div
				data-fabric-macro={attributes?.macroId}
				data-macro-body=""
				data-macro-parameters={serializedMacroParameters}
				data-testid="blog-posts-macro"
			>
				{content}
				{contentMode === 'entire' && hasNextPage && loadMore && (
					<Box paddingBlockStart="space.200">
						<Button onClick={loadMore} isLoading={isLoadingMore}>
							<FormattedMessage {...i18n.loadMore} />
						</Button>
					</Box>
				)}
				{loadMoreError && (
					<Box paddingBlockStart="space.100">
						<Text color="color.text.danger">
							<FormattedMessage {...i18n.error} />
						</Text>
					</Box>
				)}
			</div>
			<MacroExperienceSuccess
				name={experienceName}
				contentId={contentId}
				extensionKey={extensionKey}
				mode={mode}
				attributes={attributes}
			/>
		</Fragment>
	);
};

const renderErrorFallback = () => <FormattedMessage {...i18n.error} />;

export const BlogPostsMacroRenderer = (props: BlogPostsMacroRendererProps): React.JSX.Element => (
	<GenericErrorBoundary
		renderOnError={renderErrorFallback}
		attribution={Attribution.PAGE_EXTENSIONS}
	>
		<BlogPostsMacroRendererComponent {...props} />
	</GenericErrorBoundary>
);
